<template>
  <component 
    ref="container"
    :is="tag" 
    v-bind="bindProps"
  ><slot></slot></component>
</template>

<script>
import UrlParser from 'url-parse'
import axios from 'axios';

const DEFAULT_IMG = '/static/images/common/default.jpg';
export default {
  name: 'cx-img',
  props: {
    tag: {
      type: String,
      default: "img"
    },
    imgSrc: {
      type: String,
      default: ""
    },
    srcSet: {
      type: String,
      default: ""
    },
    lazyLoad: {
      type: Boolean,
      default: false,
    },
    lazyLoadMargin: {
      type: Number,
      default: 100,
    },
    lazyLoadThreshold: {
      type: Number,
      default: 0
    },
    svg: {
      type: Boolean,
      default: false
    },
    ignoreBnw: {
      type: Boolean,
      default: false
    },
    square: {
      type: Boolean,
      default: false,
    },
    faceFocus: {
      type: Boolean,
      default: false,
    },
    imgHolder: {
      type: Boolean,
      default: true
    },
    skipQueryParse: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      mqHandlers: {},
      currentImwidth: 0,
      isMounted: false,
      observer: null,
      observed: false,
      base64Svg: '',
      imgLoad: false,
      imwidthInit: false,
    }
  },
  computed: {
    enableBnw: function() {
      return !this.ignoreBnw && this.blackAndWhite
    },
    isImg: function() {
      return this.tag == 'img';
    },
    parseSrcSet: function() {
      return this.srcSet.trim().length > 0 ?
        this.srcSet.split(',')
          .map(pair => pair.trim().split(' ').map(m => m.trim())
        ) : []
    },
    parsedImg: function() {
      return this.parseImg(this.imgSrc, {
        ignoreBnw: this.ignoreBnw,
        faceFocus: this.faceFocus,
        square: this.square,
        skipQueryParse: this.skipQueryParse
      });
    },
    devicePixelRatio: function() {
      if(this.$isServer) {
        return 1
      } else {
        return window.devicePixelRatio || 1;
      }
    },
    bindProps: function() {
      const props = {};
      
      if (this.svg) {
        return this.isImg 
          ? { src: this.base64Svg } 
          : {
            style: {
              "backgroundImage": this.base64Svg ? `url("${this.base64Svg}")` : 'none'
            }
          }
      }

      if (this.isImg) {
        props.src = this.imwidthInit 
          && (((this.lazyLoad && this.observed) || !this.lazyLoad) 
          && (!this.imgHolder || this.imgLoad))
          ? this.parsedImg.toString() : DEFAULT_IMG
        /**
         * For browser don't support srcset it will takes
         * the init src only, only IE not support yet.
         */
        if (this.parseSrcSet.length > 0 && !this.skipQueryParse) {
          props.srcset = this.parseSrcSet.map(
            ([imwidth, query]) => {
              this.parsedImg.query.imwidth = imwidth;
              return `${this.parsedImg.toString()} ${query}`;
            }
          ).join(',')
        }
      } else {
        if (this.currentImwidth > 0 && !this.skipQueryParse) {
          this.parsedImg.query.imwidth = this.currentImwidth * this.devicePixelRatio;
        }
        props.style = {
          'backgroundImage': (this.isMounted && this.imwidthInit) ? `url(${
            (((this.lazyLoad && this.observed) || !this.lazyLoad) && (!this.imgHolder || this.imgLoad))
              ? this.parsedImg.toString() : DEFAULT_IMG
          })` : 'none'
        }
      }

      return props;
    }
  },
  methods: {
    loadImage: function() {
      console.log('[cxImg] LOAD IMG')
      this.imgLoad = false;
      this.loadFail = false;

      const img = new Image();
      const vm = this;

      img.onload = () => {
        console.log('[cxImg] loaded');
        vm.imgLoad = true;
      }

      img.onerror = () => {
        console.log('[cxImg] fail to load');
        vm.imgLoad = true;
      }

      img.src = this.parsedImg.toString();
    },
    getMQL: function(key) {
      return this.$store.getters['ui/getMQL'](key)
    },
    initMQL: function() {
      let minImwidth = 0;
      this.parseSrcSet.forEach(([imwidth, query]) => {
        const vm = this;
        if (query.endsWith('w')) {
          if (imwidth < minImwidth || minImwidth == 0) {
            minImwidth = imwidth;
          }
          if (window.innerWidth > query.slice(0, -1)) {
            if (imwidth > this.currentImwidth) {
              this.currentImwidth = imwidth;
              this.imwidthInit = true;
            }
          } else {
            const key = `(min-width: ${query.slice(0, -1)}px)`;
            let mql = this.getMQL(key);
            if (!mql) {
              this.$store.dispatch('ui/ADD_MQL', {
                key,
                mql: window.matchMedia(key)
              })
            }

            setTimeout(() => {
              this.mqHandlers[key] = function(e) {
                if (e.matches) {
                  if (imwidth > vm.currentImwidth) {
                    vm.currentImwidth = imwidth;
                  }
                } 
              }
              const mql = this.getMQL(key);
              mql.addListener(this.mqHandlers[key]);
            }, 0); 
          }
        }
      });
      if (this.currentImwidth == 0) {
        this.currentImwidth = minImwidth;
      }
      this.imwidthInit = true;
      this.isMounted = true;
    },
    removeMQL: function() {
      Object.keys(this.mqHandlers).forEach(key => {
        let mql = this.getMQL(key);
        if (mql) {
          mql.removeListener(this.mqHandlers[key]);
        }
      })
    },
    initObserver: function() {
      if (this.$refs 
        && this.$refs.container 
        && "IntersectionObserver" in window
      ) {
        this.observer = new IntersectionObserver((entries, observer) => {
          let {isIntersecting, intersectionRatio} = entries[0];
          if (isIntersecting) {
            this.observed = true;
            this.imgHolder && this.loadImage();
            this.observer.unobserve(this.$refs.container);
          }
        }, {
          root: null, //document.querySelector('#app'),
          rootMargin: `${this.lazyLoadMargin}px`,
          threshold: this.lazyLoadThreshold
        });
        this.observer.observe(this.$refs.container);
      } else {
        this.observed = true;
        this.imgHolder && this.loadImage();
      }
    },
    removeObserver: function() {
      this.observer && this.observer.unobserve(this.$refs.container);
    }
  },
  created() {
    if (this.svg && !this.enableBnw) {
      this.base64Svg = this.imgSrc
    }
  },
  mounted() {
    if (this.svg && this.enableBnw) {
      axios.get(this.imgSrc)
        .then((res) => {
          let ADD_GRAY_FILTER = '<style>:-ms-input-placeholder, svg > *{filter:url(#grayscale)} svg{filter:grayscale(1)}</style><filter id="grayscale"><feColorMatrix type="matrix" color-interpolation-filters="sRGB" values="0.2126 0.7152 0.0722 0 0 0.2126 0.7152 0.0722 0 0 0.2126 0.7152 0.0722 0 0 0 0 0 1 0"/></filter>'
          
          let svg = res.data;
          let svgStart = svg.indexOf('<svg ');
          svg = svg.substring(svgStart);
          let defsStart = svg.indexOf('<defs>');
          if (defsStart == -1) {
            ADD_GRAY_FILTER = `<defs>${ADD_GRAY_FILTER}</defs>`
            defsStart = svg.indexOf('>') + 1
          } else {
            defsStart += 6
          }

          svg = svg.substring(0, defsStart) + ADD_GRAY_FILTER + svg.substring(defsStart);

          // if (this.isImg) {
            // this.base64Svg = `data:image/svg+xml;utf8, ${svg.replace(/#/g, '%23')}`;
          // } else {
            let b64Str = window.btoa(svg);
            while (b64Str.length % 4 > 0) {
              b64Str += '=';
            }
            this.base64Svg = `data:image/svg+xml;base64, ${b64Str}`;
          // }
        }).catch((e) => {
          console.log(e);
        })
    }
    this.initMQL();
    if (this.lazyLoad) {
      this.initObserver();
    } else {
      this.imgHolder && this.loadImage();
    }
  },
  beforeDestroy() {
    this.removeMQL();
    this.lazyLoad && this.removeObserver();
  },
}
</script>
